import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY, IS_SA } from '../../../../../../config/appConfig';
import { FinancialRatiosType } from '../../../../../../constants/financialRatiosTypes';
import { currencyFormatterHelper } from '../../../../../../helpers/currencyFormatterHelper';
import { DateHelper } from '../../../../../../helpers/dateHelper';
import { FormatNumberArray } from '../../../../../../helpers/formatNumberArray';
import { CustomTooltip } from '../../../../../../shared/components/common/tooltip';
import { MarketplaceService } from '../../marketplaceService';

const zeroValues = ["0", "0.00M", "0.0M", "0M", "0.0%", "0.00"];

export const BusinessFinancialRatios = ({ businessDetails = null, onLoaded = null }) => {
	const marketplaceService = new MarketplaceService();
	const [financialRatios, setFinancialRatios] = useState([]);
	const [financialDetails, setFinancialDetails] = useState({});
	const [checkAllZero, setCheckAllZero] = useState(false);

	const { t } = useTranslation();

	useEffect(() => {
		if (businessDetails && businessDetails?.borrowerId) {
			loadFinancialRatios();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessDetails]);

	const formatValue = (value) => {
		let formattedValue = 0;
		if (value) {
			if (!isNaN(Number(value))) {
				formattedValue = !value.includes(',')
					? currencyFormatterHelper(value)
					: value;
			} else {
				formattedValue = value;
			}
		}
		return formattedValue;
	};

	const loadFinancialRatios = () => {
		marketplaceService
			.getFinancialRatios(businessDetails?.borrowerId)
			.then((resp) => {
				let respData = FormatNumberArray(resp.data?.ratios, ['periodEnding', 'type']);
				setFinancialDetails(resp.data);
				if (onLoaded) onLoaded({ limitsOnDueDiligence: resp.data?.limitsOnDueDiligence });
				if (respData?.length) {
					let ratios = respData;
					ratios.forEach((ratio) => {
						Object.keys(ratio).forEach((key) => {
							if (key === 'periodEnding' || key === 'type') {
							} else {
								ratio[key] = formatValue(ratio[key]);
							}
						});
					});
					checkYearToDateEmpty(ratios);
				}
			})
			.catch((err) => { console.log(err); });
	};

	const checkYearToDateEmpty = (ratios = []) => {
		let ratiosValues = ratios;
		setCheckAllZero(checkRatiosValidity(ratiosValues));
		ratiosValues = ratiosValues.reverse();
		setFinancialRatios([...ratiosValues]);
	};

	const checkRatiosValidity = (ratios) => {
		return ratios.every(ratio => {
			return Object.keys(ratio).every(key => {
				if (["periodEnding", "type", "includesAuditedFigures"].includes(key)) {
					return true;
				}
				const value = ratio[key];
				return value === null || zeroValues.includes(value?.toString());
			});
		});
	};

	const checkFiledisEmpty = (key) => {
		return financialRatios.every(obj =>
			!obj[key] ||
			obj[key] === 0 ||
			obj[key] === "0" ||
			obj[key] === "0.00M" ||
			obj[key] === "0.0M" ||
			obj[key] === "0M" ||
			obj[key] === '0.0%' ||
			obj[key] === "0.00"
		);
	};

	const checkGrowth = () => {
		if (financialRatios && financialRatios.length && financialRatios?.length === 2) {
			return !!!financialRatios.find(resp => resp.type === 1);
		}
		return true;
	};

	const showColumns = (periodEnding) => {
		if (!periodEnding) {
			return false;
		}
		return financialRatios.find((resp) => resp?.periodEnding === periodEnding);
	};

	const checkIfDate = (period) => {
		const date = new Date(period);
		if (date && !isNaN(date?.getTime())) {
			return DateHelper.formatDate(date);
		}
		return period;
	};

	const formatNumbers = (value) => {
		if (!value) return 0;

		if (typeof value === 'number') {
			return value >= 0 ? currencyFormatterHelper(value) : 'N/A';
		}

		const numeric = parseFloat(value.replace(/[% ,]/g, ''));

		if (isNaN(numeric)) {
			return value;
		}
		return numeric >= 0 ? currencyFormatterHelper(value) : 'N/A';
	};

	const getRevenueGrowth = (value) => {
		if (!value && value !== 0) {
			return '0.0%';
		}

		if (zeroValues.includes(value?.toString())) {
			return 'N/A';
		}

		return currencyFormatterHelper(value);
	};

	// Parse formatted value back to a number for comparison (direction only)
	const parseFormattedValue = (value) => {
		if (value === null || value === undefined || value === '') return null;
		const str = String(value).trim();
		if (['N/A', '0', '0.00', '0.0M', '0.00M', '0M', '0.0%'].includes(str)) return null;
		let cleaned = str.replace(/%/g, '').replace(/,/g, '').trim();
		if (cleaned.endsWith('M')) {
			const num = parseFloat(cleaned.slice(0, -1));
			return isNaN(num) ? null : num;
		}
		const num = parseFloat(cleaned);
		return isNaN(num) ? null : num;
	};

	const getChangeIndicator = (newerValue, olderValue) => {
		const newer = parseFormattedValue(newerValue);
		const older = parseFormattedValue(olderValue);
		if (newer === null || older === null || newer === older) return null;
		return newer > older
			? <span className="text-investor-green mr-1">↑</span>
			: <span className="text-red-500 mr-1">↓</span>;
	};

	// Renders the dynamic cells for a row, with change arrow in the newest (index 0) column
	const renderDynamicCells = (getValue, getFieldValue) => {
		const olderRatio = financialRatios.length >= 2 ? financialRatios[1] : null;
		return financialRatios.map((ratio, index) => {
			if (!showColumns(ratio?.periodEnding)) return null;
			const display = getValue(ratio);
			return (
				<TableCell key={index} className="!font-bold">
					{index === 0 && olderRatio ? (
						<Box className="flex items-center">
							{getChangeIndicator(getFieldValue ? getFieldValue(ratio) : display, getFieldValue ? getFieldValue(olderRatio) : getValue(olderRatio))}
							{display}
						</Box>
					) : display}
				</TableCell>
			);
		});
	};

	return financialRatios?.length && !checkAllZero ? (
		<Box className="bg-gray-100 rounded-3xl py-10 px-12">
			{/* Heading section — separated from table */}
			<Box className="flex flex-col mb-6">
				<Box className="text-2xl font-black text-investor-deep">
					{t('financialHighlight')}
				</Box>
				{financialDetails?.type === FinancialRatiosType.Audited ||
					financialDetails?.type === FinancialRatiosType.Internal ? (
					<Box className="text-sm font-normal text-investor-deep">
						{financialDetails?.type === FinancialRatiosType.Audited
							? t('basedOnAudit')
							: t('basedOnInternal')}
					</Box>
				) : null}
			</Box>

			<Table sx={{ '& th, & td': { fontSize: '12px' } }}>
				<TableHead>
					<TableRow
						sx={{
							'& th': {
								backgroundColor: '#96ECE0',
								color: '#063236',
								fontWeight: 700,
								borderBottom: 'none',
							},
							'& th:first-of-type': {
								borderTopLeftRadius: '20px',
								borderBottomLeftRadius: '20px',
							},
							'& th:last-of-type': {
								borderTopRightRadius: '20px',
								borderBottomRightRadius: '20px',
							},
						}}
					>
						<TableCell></TableCell>
						{financialRatios.map((ratio, index) => {
							return (
								<>
									{showColumns(ratio?.periodEnding) ? (
										<TableCell key={index}>
											{checkIfDate(ratio?.periodEnding)}
										</TableCell>
									) : null}
								</>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody sx={{ '& td': { borderBottomColor: '#063236' } }}>
					{!checkFiledisEmpty("revenueBand") ? <TableRow>
						<TableCell>{<span>
							<span>{t('revenueBand')}</span> <span className='mx-0.5 currency-symbol'> ({CURRENCY})</span>
						</span>}</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.revenueBand),
							(r) => r?.revenueBand
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("netProfitBand") ? <TableRow>
						<TableCell>{<span>
							<span>{t('netProfitBand')}</span> <span className='mx-0.5 currency-symbol'> ({CURRENCY})</span>
						</span>}</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.netProfitBand),
							(r) => r?.netProfitBand
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("revenueGrowth") && checkGrowth() ? <TableRow>
						<TableCell>{`${t('revenueGrowth')}`}</TableCell>
						{renderDynamicCells(
							(r) => getRevenueGrowth(r?.revenueGrowth),
							(r) => r?.revenueGrowth
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("netProfitMargin") ? <TableRow>
						<TableCell>
							{`${t('netProfitMargin')}`}
							<CustomTooltip id="netProfitMargin" text={t('netProfitMarginHint')}></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.netProfitMargin),
							(r) => r?.netProfitMargin
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("returnOnEquity") ? <TableRow>
						<TableCell>
							{`${t('returnOnEquity')}`}
							<CustomTooltip id={'returnOnEquity'} text={t('returnOnEquityHint')}></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.returnOnEquity),
							(r) => r?.returnOnEquity
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("returnOnAssets") ? <TableRow>
						<TableCell>
							{`${t('returnOnAssets')}`}
							<CustomTooltip id="returnOnAssets" text={t('returnOnAssetsHint')}></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.returnOnAssets),
							(r) => r?.returnOnAssets
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("totalOutstandingDebt") ? <TableRow>
						<TableCell>{t('totalOutstandingDebtFinance')}</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.totalOutstandingDebt),
							(r) => r?.totalOutstandingDebt
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("inventoryDays") ? <TableRow>
						<TableCell>
							{t('inventoryDays')}
							<CustomTooltip text={t('inventoryDaysHint')} id="inventoryDays"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => r?.inventoryDays,
							(r) => r?.inventoryDays
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("debtorDays") ? <TableRow>
						<TableCell>
							{t('debtorDays')}{' '}
							<CustomTooltip text={t('debtorDaysHint')} id="debtorDays"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => r?.debtorDays,
							(r) => r?.debtorDays
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("creditorDays") ? <TableRow>
						<TableCell>
							{t('creditorDays')}{' '}
							<CustomTooltip text={t('creditorDaysHint')} id="creditorDays"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => r?.creditorDays,
							(r) => r?.creditorDays
						)}
					</TableRow> : null}
					{!checkFiledisEmpty("levelOfExternalDebt") ? <TableRow>
						<TableCell>
							<span>
								<span>{t('levelofExternalDebt')}</span> <span className='mx-0.5 currency-symbol'> ({CURRENCY})</span>
							</span>
							<CustomTooltip text={t('levelofExternalDebtHint')} id="levelOfExternalDebt"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => currencyFormatterHelper(r?.levelOfExternalDebt),
							(r) => r?.levelOfExternalDebt
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('debtRatio') ? <TableRow>
						<TableCell>
							{`${t('debtRatio')}`}{' '}
							<CustomTooltip text={t('debtRatioHint')} id="debtRatio"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.debtRatio),
							(r) => r?.debtRatio
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('interestCoverRatio') ? <TableRow>
						<TableCell>
							{t('interestCoverRatio')}{' '}
							<CustomTooltip text={t('interestCoverRatioHint')} id="interestCoverRatio"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.interestCoverRatio),
							(r) => r?.interestCoverRatio
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('debtServiceCoverageRatio') ? <TableRow>
						<TableCell>
							{t('debtServiceCoverageRatio')}{' '}
							<CustomTooltip text={t('debtServiceCoverageRatioHint')} id="debtServiceCoverageRatio"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.debtServiceCoverageRatio),
							(r) => r?.debtServiceCoverageRatio
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('debtToEquity') ? <TableRow>
						<TableCell>
							{t('debtToEquity')}{' '}
							<CustomTooltip text={t('debtToEquityHint')} id="debtToEquity"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.debtToEquity),
							(r) => r?.debtToEquity
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('currentRatio') ? <TableRow>
						<TableCell>
							{t('currentRatio')}{' '}
							<CustomTooltip text={t('currentRatioHint')} id="currentRatio"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.currentRatio),
							(r) => r?.currentRatio
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('quickRatio') ? <TableRow>
						<TableCell>
							{t('quickRatio')}{' '}
							<CustomTooltip text={t('quickRatioHint')} id="quickRatio"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.quickRatio),
							(r) => r?.quickRatio
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('operatingCashflow') ? <TableRow>
						<TableCell>
							{t('operatingCashflow')}{' '}
							<CustomTooltip text={t('operatingCashflowHint')} id="operatingCashflow"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => formatNumbers(r?.operatingCashflow),
							(r) => r?.operatingCashflow
						)}
					</TableRow> : null}
					{!checkFiledisEmpty('repaymentCoverageStrength') ? <TableRow>
						<TableCell>
							{t('repaymentCoverageStrength')}
							<CustomTooltip text={t('repaymentCoverageStrengthHint')} id="repaymentCoverageStrength"></CustomTooltip>
						</TableCell>
						{renderDynamicCells(
							(r) => r?.repaymentCoverageStrength,
							(r) => r?.repaymentCoverageStrength
						)}
					</TableRow> : null}
				</TableBody>
			</Table>
		</Box>
	) : null;
};
